import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MotionConfig, useReducedMotion } from 'framer-motion';
import Lenis from 'lenis';
import usePageSEO from '../../hooks/usePageSEO';
import { DionNav } from './DionNav';
import { HeroScene, EnterHairScene, HairlineScene } from './ScenesIntro';
import { EngineeringScene, LongHairScene } from './ScenesTech';
import { TreatmentsScene, MethodsScene } from './ScenesExplore';
import { DonorScene, MedicineEngineeringScene, ResultsScene } from './ScenesProof';
import { ProcessScene, FinalScene, DionFooter } from './ScenesClose';
import './dion.css';

export default function DionExperience() {
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const lenisRef = useRef(null);

  usePageSEO({
    title: 'DION HAIR CLINIC | Ästhetik × Medizin × Ingenieurtechnik',
    description: 'Cinematische Erlebnisseite der Dion Hair Clinic. Haartransplantation mit ästhetischem Anspruch – Präzision, Planung und Natürlichkeit bis ins Detail.',
    canonical: 'https://www.dionhairclinic.de/experience',
    ogTitle: 'DION HAIR CLINIC | Ihr Haar. Ihr Ausdruck.',
    ogDescription: 'Haartransplantation mit ästhetischem Anspruch. Präzision. Planung. Natürlichkeit.',
    ogUrl: 'https://www.dionhairclinic.de/experience',
  });

  useEffect(() => {
    document.documentElement.classList.add('dion-exp-active');
    let raf;
    if (!reduced) {
      const lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 0.9 });
      lenisRef.current = lenis;
      const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    }
    return () => {
      document.documentElement.classList.remove('dion-exp-active');
      if (raf) cancelAnimationFrame(raf);
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, [reduced]);

  const onNavigate = useCallback((id) => {
    const target = id === 'dion-top' ? 0 : document.getElementById(id);
    if (lenisRef.current && !reduced) {
      lenisRef.current.scrollTo(target, { offset: id === 'dion-top' ? 0 : -70, duration: 1.3 });
    } else if (target === 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [reduced]);

  const onConsult = useCallback(() => navigate('/#kontakt'), [navigate]);

  return (
    <MotionConfig reducedMotion="user">
      <div className="dion-exp dh-grain" data-testid="dion-experience" lang="de">
        <a href="#dion-main" className="dh-skip" data-testid="dion-skip-link">Zum Inhalt</a>
        <DionNav onNavigate={onNavigate} onConsult={onConsult} />
        <main id="dion-main">
          <HeroScene onConsult={onConsult} onNavigate={onNavigate} />
          <EnterHairScene />
          <HairlineScene />
          <EngineeringScene />
          <LongHairScene />
          <TreatmentsScene />
          <MethodsScene />
          <DonorScene />
          <MedicineEngineeringScene />
          <ResultsScene />
          <ProcessScene />
          <FinalScene onConsult={onConsult} />
        </main>
        <DionFooter onTop={() => onNavigate('dion-top')} />
      </div>
    </MotionConfig>
  );
}
